// 위젯 추가 시 삽입 위치·팔레트 게이트 규칙 — 순수 함수.
//
// 위젯 추가/배치 위치(2026-07-17 사용자 확정 — 일반 편집기 관례, handleWidgetPaste의
// "컨테이너=안 말미 / 리프=anchor 다음" 규칙과 동형):
//  - anchor(우클릭 대상, 다중 선택이면 주 선택)가 일반 위젯 → anchor **바로 다음**(같은 부모 형제, afterId)
//  - anchor가 children 컨테이너(섹션·모달·반복) → 그 안 말미("안에 추가" 의도)
//  - anchor 없음 → 루트 말미
// 빈 영역(canvas) 우클릭의 모달/isolation 컨텍스트 fallback까지 포함한 전체 규칙은
// resolveWriteAddPlacement(EditorPage는 트리·페이지 상태를 인자로 넘기기만 한다).

import type { ComposerWidget } from '@safetyhub/shared';

/**
 * "안에 추가"로 취급하는 컨테이너 kind.
 * ⚠️ modalTrigger·repeat가 반드시 포함돼야 한다 — 트리 행의 "+위젯"이 제거되고 우클릭이 유일한
 * 추가 경로가 되면서(2026-07-22), group만 인정하면 **모달 콘텐츠·반복 행 템플릿에 위젯을 넣을
 * 수단이 아예 없어진다**. branch는 슬롯 단위라 제외(슬롯 행의 "+"가 정규 경로).
 */
export const ADD_INSIDE_CONTAINER_KINDS: readonly ComposerWidget['kind'][] = [
  'group',
  'modalTrigger',
  'repeat',
];

/** anchor가 "안에 추가" 컨테이너인가. */
export function isAddInsideContainer(kind: ComposerWidget['kind'] | null | undefined): boolean {
  return kind != null && ADD_INSIDE_CONTAINER_KINDS.includes(kind);
}

/**
 * target==='selection'일 때의 삽입 위치.
 * @param args.anchorParentId widgetParentId 결과(undefined=트리 미발견 → 루트 취급).
 */
export function resolveAddPlacement(args: {
  anchorKind: ComposerWidget['kind'] | null | undefined;
  anchorId: string | null | undefined;
  anchorParentId: string | null | undefined;
}): { parentId: string | null; afterId: string | null } {
  const { anchorKind, anchorId, anchorParentId } = args;
  if (!anchorId) return { parentId: null, afterId: null };
  if (isAddInsideContainer(anchorKind)) return { parentId: anchorId, afterId: null };
  return { parentId: anchorParentId ?? null, afterId: anchorId };
}

/**
 * 우클릭 '위젯 추가'의 삽입 위치 전체 규칙 — anchor 선택(승격 전 원시 대상 우선) + fallback까지.
 *
 * - 삽입 anchor는 **가리킨 대상 우선**(rawAddAnchor) — 그룹 우선 선택(isolation)이 선택을 상위
 *   컨테이너로 승격시켜도 "반복을 가리켰는데 반복의 형제로 들어가는" 어긋남이 없도록
 *   (2026-07-22 사용자 확정). 단 트리에 없는 대상(표 셀 안 위젯 등)은 순서/부모 개념이 없으므로
 *   선택(selectedAnchor) 기준으로 폴백한다 — `parentIdOf`가 `undefined`(미발견)일 때만이고,
 *   `null`(루트 위젯)은 정상 발견이므로 폴백하지 않는다.
 * - anchor가 정해지면 위치는 resolveAddPlacement가 정본.
 * - 빈 영역(canvas)이거나 anchor가 없으면: 모달 저작 중이면 그 모달이 작업 스코프다. 빈 영역
 *   우클릭 핸들러가 선택/컨텍스트를 리셋(setWriteSelectionContextId(null))하고 메뉴를 열기 때문에
 *   이 fallback이 없으면 "모달에서 아무것도 선택하지 않고 위젯 추가" → 모달이 아니라 **루트
 *   캔버스**에 붙는다(2026-07-22 사용자 제보). 선택 semantics는 건드리지 않고 추가 대상만 고정.
 *
 * @param args.parentIdOf 위젯 id → 부모 id(null=루트, undefined=트리 미발견).
 */
export function resolveWriteAddPlacement(args: {
  /** 'selection'=위젯 우클릭 / 'canvas'=빈 영역 우클릭 */
  target: 'selection' | 'canvas';
  /** 현재 선택(anchor) 위젯 — 없으면 null */
  selectedAnchor: ComposerWidget | null;
  /** 우클릭 원시 대상(승격 전) 위젯 — 없으면 null */
  rawAddAnchor: ComposerWidget | null;
  parentIdOf: (id: string) => string | null | undefined;
  /** 모달 저작 중이면 그 modalTrigger id */
  modalAuthoringId: string | null;
  /** isolation 컨텍스트(섹션) id */
  writeSelectionContextId: string | null;
}): { parentId: string | null; afterId: string | null } {
  const {
    target,
    selectedAnchor,
    rawAddAnchor,
    parentIdOf,
    modalAuthoringId,
    writeSelectionContextId,
  } = args;
  const addAnchor =
    rawAddAnchor && parentIdOf(rawAddAnchor.id) !== undefined ? rawAddAnchor : selectedAnchor;
  if (target === 'selection' && addAnchor) {
    return resolveAddPlacement({
      anchorKind: addAnchor.kind,
      anchorId: addAnchor.id,
      anchorParentId: parentIdOf(addAnchor.id),
    });
  }
  return {
    parentId:
      modalAuthoringId != null
        ? modalAuthoringId
        : writeSelectionContextId != null && parentIdOf(writeSelectionContextId) !== undefined
          ? writeSelectionContextId
          : null,
    afterId: null,
  };
}

/**
 * 삽입 대상 부모의 kind로 결정되는 팔레트 게이트(parentKind 미지정 = 루트).
 * - 반복(repeat): root/group/**repeat** 안 허용 — 중첩 repeat(반복 안 반복)은 안쪽 over가
 *   부모 행의 배열 필드를 읽는 정식 저작 어휘(P4, 결정 2026-08-17-composer-nested-repeat-photo-group.
 *   종전 "반복 안 중첩 반복은 over 스코프 무의미" 전제는 P1 스파이크로 무효화). 모달 안은 계속
 *   금지 — 모달 스코프(modalValues)는 행 스코프가 아니다(findEnclosingRepeatWidget innermost 리셋).
 *   (범용 빈 반복 추가 후 속성 패널에서 over 지정 → 뼈대 자동 생성, 2026-07-20 재도입 66582201e.)
 * - 모달 버튼(modalTrigger): 모달 안 모달 중첩만 금지(그 외 컨테이너·루트는 허용).
 */
export function addWidgetGates(parentKind: ComposerWidget['kind'] | null | undefined): {
  allowRepeat: boolean;
  allowModalTrigger: boolean;
} {
  return {
    allowRepeat: !parentKind || parentKind === 'group' || parentKind === 'repeat',
    allowModalTrigger: !parentKind || parentKind !== 'modalTrigger',
  };
}
